import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { useTranslation } from "react-i18next";
import '../../../node_modules/react-grid-layout/css/styles.css';
import '../../../node_modules/react-resizable/css/styles.css';
import { ensureArray, groupBy } from "../utils/arrayUtilities";
import Card from "./Card";
import Checkbox from "./Checkbox";
import ColorPicker from "./ColorPicker";
import CustomCtrl, { editorComponents } from "./CustomCtrl";
import { DefaultButton } from "./DefaultButton";
import { Col, Row } from "./Grid";
import './GridEditor.css';
import Icon from "./Icon";
import IconPicker from "./IconPicker";
import TextInput from "./TextInput";

import PropTypes from 'prop-types';
import { deepClone, deepEquals } from "../utils/objectUtilities";
import { ReactNodeDialog } from "../utils/dialogUtlities";
import PermissionsInput from "../../components/settings/PermissionsInput";
import { CustomCtrlDef, CustomCtrlDefModal, CustomCtrlTypeDropdown } from "./CustomCtrlDef";
import ReactForm from "./ReactForm";

const ResponsiveGridLayout = WidthProvider(Responsive);

const fieldsToCopy = ['type', 'colspan', 'required', 'readonly', 'disabled', 'hideLabel'];

const parseIconLiteral = icon => {
    if (typeof icon !== 'string') {
        return icon;
    }
    const [variant, kind] = icon.trim().split(' ');
    if (!variant || !kind) {
        return undefined;
    }
    return { variant: variant.slice(3), kind: kind.slice(3) };
}

const getIconPickerValue = icon => {
    if (!icon) {
        return undefined;
    }
    if (typeof icon === 'string') {
        return icon;
    }
    return `fa-${icon.variant} fa-${icon.kind}`;
}

/**
 * An editor for a 12 column grid of custom controls (used by the FromEditor)
 * @component
 */
const GridEditor = ({ rows, disabled = false, onChange }) => {
    const [edit, setEdit] = useState();

    const customCtrlDefs = useMemo(() => editorComponents(), []);

    const handleCellUpdate = delta => {
        if (edit && delta) {
            onChange(rows.map((row, rowIdx) => rowIdx === edit.rowIdx ? row.map((cell, cellIdx) => cellIdx === edit.cellIdx ? {...cell, ...delta} : cell) : row));
        }
        setEdit();
    }

    const handleCellDelete = (rIdx, cIdx) => {
        let newRows = rows.map((row, rowIdx) => rowIdx === rIdx ? row.filter((cell, cellIdx) => cellIdx !== cIdx) : row);
        newRows = newRows.filter(row => row.length !== 0);
        onChange(newRows);
    }

    const handleLayoutChange = layout => {
        const idToCellMap = {}
        rows.flat().map(cell => idToCellMap[cell.uuid] = cell);
        let newRows = Object.values(groupBy(layout, 'y'));
        const comparator = (l1, l2) => l1.x - l2.x;
        newRows.forEach(row => row.sort(comparator));
        newRows = newRows.map(row => row.map(cell => ({ ...idToCellMap[cell.i], colspan: cell.w })));
        if (!deepEquals(rows, newRows)) {
            onChange(newRows);
        }
    }

    return (<>
        { edit && <CustomCtrlDefModal showModal={!!edit} kind={edit.kind} value={edit.cell} onClose={handleCellUpdate}></CustomCtrlDefModal>}
        <ResponsiveGridLayout
            className="layout w-100"
            breakpoints={{ xs: 0 }}
            cols={{ xs: 12 }}
            rowHeight={96}
            compactType={'horizontal'}
            resizeHandles={disabled ? [] : ['e', 'w']}
            onLayoutChange={handleLayoutChange}
        >
            {rows.flatMap((row, rowIdx) => {
                let cellGridIdx = 0;
                return row.map((cell, cellIdx) => {
                    const colspan = cell.colspan ?? Math.max(1, Math.floor(12 / row.length));
                    let toReturn = <div className="px-3" key={cell.uuid} data-grid={{ x: cellGridIdx, y: rowIdx, w: colspan, h: 1, minW: 1, maxW: 12, minH: 1, maxH: 1, static: disabled }} style={{ display: 'table-cell', verticalAlign: 'middle' }}>
                        <Row className="h-50 align-items-center">
                            <Col>
                                <CustomCtrl def={cell} hideLabel={true} disabled={true}></CustomCtrl>
                            </Col>
                        </Row>
                        <Row className="h-50 align-items-end pb-3">
                            <Col xs="auto" className="px-2">
                                <Icon intent="move"></Icon>
                            </Col>
                            <Col className="px-2" style={{ textAlign: 'end' }}>
                                { customCtrlDefs.some(def => def.kind === cell.type) && !disabled ? <DefaultButton variant="outline-dark" size="sm" kind="edit" hideLabel={true} onClick={e => setEdit({ kind: cell.type, cell, rowIdx, cellIdx: cellIdx })} onMouseDown={e => e.stopPropagation()}></DefaultButton> : null }
                                { !disabled && <DefaultButton className="ml-2" variant="outline-dark" size="sm" kind="delete" onClick={() => handleCellDelete(rowIdx, cellIdx)} onMouseDown={e => e.stopPropagation()} hideLabel={true}></DefaultButton> }
                            </Col>
                        </Row>
                    </div>
                    cellGridIdx += colspan;
                    return toReturn;
                })
            })}
        </ResponsiveGridLayout>
    </>
    );
}

GridEditor.propTypes = {
    /**
     * An array of arrays of custom control definitions representing a form
     */
    rows: PropTypes.array,
    /**
     * A function to call with the updated matrix of custom controls whenever a change is made
     */
    onChange: PropTypes.func,

    /**
     * Force the control to be read-only
     */
    disabled: PropTypes.bool
}

export const makeSectionValid = section => {
    if (!section.uuid) {
        section.uuid = crypto.randomUUID();
    }
    section.rows?.forEach(row => {
        row.forEach(cell => {
            if (!cell.uuid) {
                cell.uuid = crypto.randomUUID();
            }
            if (cell.type === 'image' && !cell.hasOwnProperty('height')) {
                cell.height = 32;
            }
            if (cell.type === 'option' && cell.options?.every(o => typeof o === 'string')) {
                cell.options = cell.options.map(o => ({ label: o, value: o }));
            }
            ['readonly','disabled','required','hideLabel'].forEach(f => {
                if (cell[f] === undefined) {
                    cell[f] = false;
                }
            })
        })
    })
}

const duplicateSection = inputSection => {
    const section = deepClone(inputSection);
    const sourceUuid = section.uuid;
    section.uuid = crypto.randomUUID();
    if (sourceUuid && section.tab === sourceUuid) {
        section.tab = section.uuid;
    }
    section.rows?.forEach(row => {
        row?.forEach(cell => {
            if (cell) {
                cell.uuid = crypto.randomUUID();
            }
        })
    });
    return section;
}

const SectionSettingsDialog = ({ section: inputSection, hideAccess, onClose }) => {
    const { t } = useTranslation();
    const [section, setSection] = useState(() => {
        const section = deepClone(inputSection);
        if (section.fixed) {
            section.collapse = false;
        }
        return section;
    });

    const updateStyle = useCallback((sectionPart, field, value) => {
        setSection(section => ({ 
            ...section, 
            style: { 
                ...section?.style, 
                [sectionPart]: {
                    ...section?.style?.[sectionPart],
                    [field]: value
                }
            }
        }));
    }, []);

    const hasRestrictedAccess = section.access !== undefined && section.access !== false;
    const handleSave = () => {
        const toSave = { ...section };
        if (toSave.fixed) {
            toSave.collapse = false;
        }
        if (toSave.access === false) {
            toSave.access = undefined;
        }
        if (typeof toSave.tab === 'string') {
            toSave.tab = toSave.tab.trim();
            if (!toSave.tab) {
                toSave.tab = undefined;
            }
        }
        if (typeof toSave.icon === 'string') {
            toSave.icon = parseIconLiteral(toSave.icon);
        }
        onClose(toSave);
    }

    return <ReactNodeDialog title={t('formSectionSettings')} showModal={true} size="xlg" enforceFocus={false} onClose={() => onClose()} onSave={handleSave}>
        <Card title={t('layoutAndBehavior')}>
            <Row>
                <Col md={3}>
                    <Checkbox placeholder={t('fixed')} checked={!!section.fixed} onChange={() => setSection(section => ({ ...section, fixed: !section.fixed, collapse: !section.fixed ? false : section.collapse }))}></Checkbox>
                </Col>
                <Col md={3}>
                    <Checkbox placeholder={t('borderless')} checked={!!section.borderless} onChange={() => setSection(section => ({ ...section, borderless: !section.borderless }))}></Checkbox>
                </Col>
                <Col md={3}>
                    <Checkbox placeholder={t('collapse')} checked={section.collapse === true} disabled={section.fixed} onChange={() => setSection(section => ({ ...section, collapse: section.collapse !== true }))}></Checkbox>
                </Col>
                <Col md={3}>
                    <Checkbox placeholder={t('expandOnInvalid')} checked={!!section.expandOnInvalid} disabled={section.fixed || section.borderless} onChange={() => setSection(section => ({ ...section, expandOnInvalid: !section.expandOnInvalid }))}></Checkbox>
                </Col>
            </Row>
        </Card>
        <Card title={t('appearance')}>
            <Row className="mb-2">
                <Col md={6}>
                    <ColorPicker placeholder={t('headerColor')} value={section.style?.headerStyle?.color} disabled={section.borderless} onChange={color => updateStyle('headerStyle', 'color', color)}></ColorPicker>
                </Col>
                <Col md={6}>
                    <ColorPicker placeholder={t('headerBackgroundColor')} value={section.style?.headerStyle?.backgroundColor} disabled={section.borderless} onChange={backgroundColor => updateStyle('headerStyle', 'backgroundColor', backgroundColor)}></ColorPicker>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <ColorPicker placeholder={t('bodyColor')} value={section.style?.bodyStyle?.color} disabled={section.borderless} onChange={color => updateStyle('bodyStyle', 'color', color)}></ColorPicker>
                </Col>
                <Col md={6}>
                    <ColorPicker placeholder={t('bodyBackgroundColor')} value={section.style?.bodyStyle?.backgroundColor} disabled={section.borderless} onChange={backgroundColor => updateStyle('bodyStyle', 'backgroundColor', backgroundColor)}></ColorPicker>
                </Col>
            </Row>
        </Card>
        {!hideAccess && <Card title={t('access')}>
            <Row>
                <Col>
                    <Checkbox checked={hasRestrictedAccess} placeholder={t('restrictAccess')} onChange={() => setSection(section => ({ ...section, access: hasRestrictedAccess ? undefined : [] }))}></Checkbox>
                </Col>
            </Row>
            {hasRestrictedAccess && <PermissionsInput title={t('accessPermissions')} values={section.access} access={[0,1]} onApply={access => setSection(section => ({ ...section, access }))} adminPositions={[]}></PermissionsInput>}
        </Card>}
        <Card title={t('tabBar')}>
            <Row>
                <Col md={4}>
                    <Checkbox placeholder={t('inline')} checked={!!section.inline} onChange={() => setSection(section => ({ ...section, inline: !section.inline }))}></Checkbox>
                </Col>
            </Row>
            {!section.inline && <Row>
                <Col md={4}>
                    <IconPicker placeholder={t('icon')} value={getIconPickerValue(section.icon)} onChange={icon => setSection(section => ({ ...section, icon }))}></IconPicker>
                </Col>
                <Col md={4}>
                    <TextInput placeholder={t('shortname')} value={section.shortname} onChange={shortname => setSection(section => ({ ...section, shortname }))}></TextInput>
                </Col>
                <Col md={4}>
                    <TextInput placeholder={t('tabIdOverride')} value={section.tab} onChange={tab => setSection(section => ({ ...section, tab }))}></TextInput>
                </Col>
            </Row>}
        </Card>
    </ReactNodeDialog>;
}

const FormSection = ({ section, dragSection, disabled, hideAccess, onDragStart, onDragEnd, onDragEnter, onDragLeave, onDrop, onChange, onDelete, onDuplicate }) => {
    const { t } = useTranslation();
    const [editSettings, setEditSettings] = useState();
    const hasRestrictedAccess = section.access !== undefined && section.access !== false;

    const handleSettingsEdit = useCallback(updatedSection => {
        if (updatedSection) {
            onChange(updatedSection);
        }
        setEditSettings();
    }, [onChange])

    const grabHandle = <Col xs="auto" className="px-3" style={{ cursor: dragSection === section.uuid ? 'grabbing' : 'grab' }} draggable={!disabled} onDragStart={onDragStart} onDragEnd={onDragEnd}><Icon variant="solid" kind="grip-lines-vertical"></Icon></Col>
    return <>
        {editSettings && <SectionSettingsDialog section={editSettings} hideAccess={hideAccess} onClose={handleSettingsEdit}></SectionSettingsDialog>}
        <div key={section.uuid} onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={e => e.preventDefault()} onDrop={onDrop}>
            <Card fixed={true} title={<Row className="align-items-center w-100" style={{ fontWeight: 'normal'}}>
                {grabHandle}
                <Col><div className="w-100"><TextInput value={section.title} hideLabel={true} disabled={disabled} onChange={title => onChange({ title })}></TextInput></div></Col>
                {hasRestrictedAccess && <Col xs="auto" className="mr-2 d-flex align-items-center">
                    <span title={t('restrictAccess')} aria-label={t('restrictAccess')} style={{ fontSize: '.875rem' }}>
                        <Icon variant="solid" kind="user-lock"></Icon>
                    </span>
                </Col>}
                <Col xs="auto" className="mr-1"><DefaultButton variant="outline-dark" kind="settings" hideLabel={true} onClick={() => setEditSettings(section)} disabled={disabled}></DefaultButton></Col>
                {!disabled && <Col xs="auto" className="mr-1">
                    <DefaultButton variant="outline-dark" kind="copy" hideLabel={true} onClick={onDuplicate}></DefaultButton>
                </Col>}
                {!disabled && <Col xs="auto">
                    <DefaultButton variant="outline-danger" kind="delete" hideLabel={true} onClick={onDelete}></DefaultButton>
                </Col>}
                {grabHandle}
            </Row>} headerStyle={section.style?.headerStyle}>
            <GridEditor rows={section.rows} disabled={disabled} onChange={rows => onChange({ rows })}></GridEditor>
            </Card>
        </div>
    </>
}

/**
 * A component to edit a CustomForm definition
 * @component
 */
const FormEditor = ({ def: inputDef, disabled, hideAccess, onChange }) => {
    const { t } = useTranslation();
    const { t: tSpecific } = useTranslation('customCtrl');
    const { t: tCommon } = useTranslation('customCtrlCommon');
    const tDef = useCallback(key => {
        const specific = tSpecific(key);
        if (specific === key) {
            return tCommon(key);
        }
        return specific;
    }, [tSpecific, tCommon]);
    const [def, setDef] = useState(ensureArray(inputDef));
    const [dragSection, setDragSection] = useState(null);
    const [newControl, setNewControl] = useState({ });
    const formRef = useRef();

    const customCtrlDefs = useMemo(() => editorComponents(tDef), [tDef]);

    useEffect(() => {
        const toProcess = ensureArray(inputDef)
        if (toProcess?.some(s => !s.uuid || (s.rows.some(row => row.some(c => !c.uuid))))) {
            const toSave = deepClone(toProcess);
            toSave?.forEach(makeSectionValid)
            setDef(toSave);
            onChange(toSave);
        } else {
            setDef(toProcess);
        }
    }, [inputDef, onChange]);

    const sectionUpdated = useCallback((sectionIdx, delta) => {
        onChange(def.map((s, idx) => idx === sectionIdx ? { ...s, ...delta } : s))
    }, [def, onChange]);

    const duplicateFormSection = useCallback(sectionIdx => {
        const copiedSection = duplicateSection(def[sectionIdx]);
        const nextDef = [...def];
        nextDef.splice(sectionIdx + 1, 0, copiedSection);
        onChange(nextDef);
    }, [def, onChange]);

    const onSectionDragStart = (event, section) => {
        if (dragSection !== section.uuid) {
            setDragSection(section.uuid);
        }
    }

    const addNewControlToSection = (section, newControl) => {
        const lastRow = section.rows.slice(-1)[0];
        let newDef;
        if (!lastRow) {
            newDef = def.map(s => s.uuid !== section.uuid ? s : { ...s, rows: [[{...newControl}]] });
        } else {
            const totalColspan = lastRow.reduce((colspan, cur) => colspan + (cur?.colspan ?? 0), 0);
            if (totalColspan + newControl.colspan <= 12) {
                const newLastRow = [...lastRow, { ...newControl }];
                newDef = def.map(s => s.uuid !== section.uuid ? s : { ...s, rows: [...s.rows.slice(0, -1), newLastRow] });
            } else {
                newDef = def.map(s => s.uuid !== section.uuid ? s : { ...s, rows: [...s.rows, [{...newControl}]] });
            }
        }
        setDef(newDef);
        return newDef;
    }

    const resetNewControl = () => {
        let toUpdate = { ...newControl };
        delete toUpdate.field;
        delete toUpdate.label;
        delete toUpdate.uuid;
        const defaults = customCtrlDefs.find(def => def.kind === newControl.type)?.defaults;
        if (defaults) {
            toUpdate = { ...toUpdate, ...deepClone(defaults) }
        }
        setNewControl(toUpdate);
    }

    const sectionDragEnter = index => {
        if (dragSection) {
            const sections = [...def];
            const item = sections.splice(def.findIndex(s => s.uuid === dragSection), 1)[0];
            sections.splice(index, 0, item);
            setDef(sections);
        } else if (newControl.uuid) {
            const section = def[index];
            if (section.rows.flat(2).every(cell => cell.uuid !== newControl.uuid)) {
                addNewControlToSection(section, newControl);
            }
        }
    }

    const sectionDragLeave = index => {
        if (newControl.uuid) {
            const section = def[index];
            if (section.rows.some(row => row.some(cell => cell.uuid === newControl.uuid))) {
                let newDef = def.map(s => s.uuid !== section.uuid ? s : { ...s, rows: s.rows.map(r => r.filter(cell => cell.uuid !== newControl.uuid))})
                newDef = newDef.map(s => s.rows.some(r => r.length === 0) ? { ...s, rows: s.rows.filter(r => r.length !== 0) } : s);
                setDef(newDef);
                
            }
        }
    }

    const onSectionDrop = () => {
        if (dragSection) {
            setDragSection(null);
            onChange(def);
        } else if (newControl.uuid) {
            onChange(def);
            resetNewControl()
        }
    }

    const onSectionDragEnd = () => {
        if (dragSection !== null) {
            setDef(inputDef);
            setDragSection(null);
        }
    }

    const onNewControlDragStart = (event) => {
        if (formRef.current.checkValidity()) {
            setNewControl({ ...newControl, uuid: crypto.randomUUID() });
            event.dataTransfer.effectAllowed = "move";
        }
    }

    const onNewControlDragEnd = event => {
        if (newControl.uuid) {
            const toUpdate = { ...newControl };
            delete toUpdate.uuid;
            setNewControl(toUpdate);
        }
    }

    const addControl = () => {
        if (formRef.current?.checkValidity()) {
            const newDef = addNewControlToSection(def.at(-1), {...newControl, uuid: crypto.randomUUID() });
            onChange(newDef);
            resetNewControl();
        }
    }

    const setControlType = type => {
        let toUpdate = { };
        fieldsToCopy.forEach(f => toUpdate[f] = newControl[f]);
        const defaults = customCtrlDefs.find(def => def.kind === newControl.type)?.defaults;
        if (defaults) {
            toUpdate = { ...toUpdate, ...deepClone(defaults) }
        }
        toUpdate.type = type;
        setNewControl(toUpdate);
    }

    const updateControlForm = useCallback(delta => {
        setNewControl(newControl => ({...newControl, ...delta}));
    }, []);

    return <>
        {!disabled && <Row>
            <Col>
                <Row className="align-items-stretch">
                    <Col>
                        <div className="h-100 p-2 m-1" style={{ border: '1px solid lightgrey', borderRadius: '4px' }}>
                            <Row>
                                <Col>
                                    <span style={{ fontWeight: 'bold' }}>New Control</span>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <CustomCtrlTypeDropdown value={newControl.type} placeholder="Control Type" onChange={value => setControlType(value)}></CustomCtrlTypeDropdown>
                                </Col>
                            </Row>
                            { newControl.type ? <Row>
                                <Col>
                                    <ReactForm ref={formRef}>
                                        <CustomCtrlDef kind={newControl.type} value={newControl} onChange={updateControlForm}></CustomCtrlDef>
                                    </ReactForm>
                                </Col>
                            </Row> : null }
                            <Row>
                                <Col className="text-end mt-2">
                                    <DefaultButton kind="add" disabled={!def || def?.length === 0} onClick={addControl} style={{ cursor: (!def || def.length === 0) ? 'not-allowed' : 'move' }} draggable={def?.length > 0} onDragStart={onNewControlDragStart} onDragEnd={onNewControlDragEnd}></DefaultButton>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                    <Col xs="auto">
                        <div className="h-100 p-2 m-1 d-flex align-items-center" style={{ border: '1px solid lightgrey', borderRadius: '4px' }}>
                            <DefaultButton kind="add" label={t('addSection')} onClick={() => onChange([...(def || []), { title: '', uuid: crypto.randomUUID(), rows: [[]] }])}></DefaultButton>
                        </div>
                    </Col>
                </Row>
            </Col>
        </Row>}
        <Row>
            <Col>
                {def?.map((section, sectionIdx) => <FormSection key={section.uuid} section={section} dragSection={dragSection} disabled={disabled} onDragEnter={e => sectionDragEnter(sectionIdx)} onDragLeave={e => sectionDragLeave(sectionIdx)} onDragStart={e => onSectionDragStart(e, section)} onDragEnd={onSectionDragEnd} onDrop={onSectionDrop} onChange={delta => sectionUpdated(sectionIdx, delta)} onDelete={() => onChange(def.filter((_, idx) => idx !== sectionIdx))} onDuplicate={() => duplicateFormSection(sectionIdx)} hideAccess={hideAccess}></FormSection>)}
            </Col>
        </Row>
    </>;
}

FormEditor.propTypes = {
    /**
     * The form definition (an array of section objects)
     */
    def: PropTypes.array,

    /**
     * A callback to be sent the updated form definition after each update
     */
    onChange: PropTypes.func,

    /**
     * Force the editor to be read-only
     */
    disabled: PropTypes.bool
}

export default FormEditor;